import React, { useMemo, useRef, useState } from 'react';
import {
    Chart as ChartJS,
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import {
    AlertTriangle, CheckCircle, Download, ArrowLeft, Loader2, Send,
    ChevronDown, ChevronUp, Target, Lightbulb, TrendingUp, Award
} from 'lucide-react';
import BackNav from './components/BackNav';
import apiService from '../../../services/api';

// Register ChartJS components
ChartJS.register(
    RadialLinearScale,
    PointElement,
    LineElement,
    Filler,
    Tooltip,
    Legend
);

// Maturity Level Descriptions
const MATURITY_LEVELS = {
    1: { name: 'Inceptive', color: 'bg-red-100 text-red-700 border-red-200', description: 'Practices are nascent or non-existent. Immediate attention required.' },
    2: { name: 'Transient', color: 'bg-amber-100 text-amber-700 border-amber-200', description: 'Basic practices exist but are inconsistent. Needs systematic implementation.' },
    3: { name: 'Stable', color: 'bg-blue-100 text-blue-700 border-blue-200', description: 'Documented practices with regular implementation. Room for optimization.' },
    4: { name: 'Dynamic', color: 'bg-green-100 text-green-700 border-green-200', description: 'Best-in-class practices with continuous improvement. Exemplary level.' }
};

const SQAAFScoreReport = ({ audit, domains, onBack, onSubmitSuccess }) => {
    const reportRef = useRef(null);
    const [isExporting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [expandedDomain, setExpandedDomain] = useState(null);

    // Submit Audit Handler
    const handleSubmitAudit = async () => {
        if (!audit?._id && !audit?.audit_id) return;

        setIsSubmitting(true);
        try {
            const result = await apiService.sqaaf.submitAudit(audit._id || audit.audit_id);
            if (result.success) {
                alert('Audit submitted successfully!');
                setShowConfirmModal(false);
                if (onSubmitSuccess) onSubmitSuccess(result);
            }
        } catch (error) {
            console.error('Submit failed:', error);
            alert('Failed to submit audit. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // PDF Download Handler — print-window approach (no html2canvas memory limits)
    const handleDownloadPDF = () => {
        // Capture radar chart canvas as base64 image directly (no library needed)
        let radarImgSrc = '';
        const canvasEl = reportRef.current?.querySelector('canvas');
        if (canvasEl) {
            try { radarImgSrc = canvasEl.toDataURL('image/png'); } catch (e) {}
        }

        const MATURITY = {
            1: { name: 'Inceptive',  color: '#ef4444', bg: '#fef2f2' },
            2: { name: 'Transient',  color: '#f59e0b', bg: '#fffbeb' },
            3: { name: 'Stable',     color: '#3b82f6', bg: '#eff6ff' },
            4: { name: 'Dynamic',    color: '#10b981', bg: '#f0fdf4' },
        };

        const totalStrengths = domainScores.reduce((sum, d) => sum + d.strengths.length, 0);
        const totalGaps      = domainScores.reduce((sum, d) => sum + d.gaps.length, 0);
        const overallMaturity = Math.round(domainScores.reduce((sum, d) => sum + d.score, 0) / (domainScores.length || 1)) || 1;
        const submittedStr = audit?.status === 'submitted' && audit.submitted_at
            ? `Submitted ${new Date(audit.submitted_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}`
            : 'Draft – not yet submitted';
        const dateStr = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

        // Build each domain section
        let domainsHtml = '';
        domainScores.forEach((domain, idx) => {
            const m = MATURITY[domain.maturityLevel] || MATURITY[1];
            const scorePercent = Math.round((domain.score / 4) * 100);

            const strengthsHtml = domain.strengths.length
                ? domain.strengths.map(ind =>
                    `<li class="ind-item green-item"><span class="lvl-tag green-tag">L${ind.level}</span>${ind.statement}</li>`
                  ).join('')
                : '<li class="ind-empty">No indicators at Level 3–4 yet</li>';

            const gapsHtml = domain.gaps.length
                ? domain.gaps.map(ind =>
                    `<li class="ind-item amber-item"><span class="lvl-tag amber-tag">L${ind.level}</span>${ind.statement}</li>`
                  ).join('')
                : '<li class="ind-empty">No major gaps identified – excellent performance</li>';

            domainsHtml += `
            <div class="domain-block">
                <div class="domain-top">
                <div class="domain-header" style="border-left:4px solid ${m.color}">
                    <div class="domain-id-badge" style="background:${m.bg};color:${m.color}">${domain.id}</div>
                    <div class="domain-info">
                        <div class="domain-name">${domain.name}</div>
                        <div class="domain-meta">
                            <span class="lvl-pill" style="background:${m.bg};color:${m.color};border:1px solid ${m.color}40">
                                L${domain.maturityLevel} – ${m.name}
                            </span>
                            <span class="meta-dot">•</span>
                            <span>${domain.answered}/${domain.totalIndicators} indicators answered</span>
                        </div>
                    </div>
                    <div class="domain-avg" style="color:${m.color}">
                        ${domain.score.toFixed(1)}<span class="domain-avg-sub">/ 4.0</span>
                    </div>
                </div>

                <div class="bar-row">
                    <div class="bar-track">
                        <div class="bar-fill" style="width:${scorePercent}%;background:${m.color}"></div>
                    </div>
                    <span class="bar-pct" style="color:${m.color}">${scorePercent}%</span>
                </div>
                </div><!-- /.domain-top -->

                <div class="two-col">
                    <div class="col-panel green-panel">
                        <div class="col-title green-title">&#10003; Strengths (${domain.strengths.length})</div>
                        <ul class="ind-list">${strengthsHtml}</ul>
                    </div>
                    <div class="col-panel amber-panel">
                        <div class="col-title amber-title">&#9651; Areas for Improvement (${domain.gaps.length})</div>
                        <ul class="ind-list">${gapsHtml}</ul>
                    </div>
                </div>
            </div>`;
        });

        const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8"/>
<title>SQAAF Report – ${audit?.academic_year || ''}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    font-family: 'Poppins', sans-serif;
    font-size: 11px;
    color: #1e293b;
    background: #fff;
    line-height: 1.7;
    print-color-adjust: exact;
    -webkit-print-color-adjust: exact;
  }
  @page { size: A4; margin: 16mm 14mm; }

  /* ── Header Banner ── */
  .header-banner {
    background: linear-gradient(135deg, #D67C2C, #e8973e);
    color: #fff;
    border-radius: 12px;
    padding: 20px 24px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 18px;
    page-break-inside: avoid;
  }
  .banner-score { font-size: 42px; font-weight: 700; line-height: 1; margin: 4px 0 8px; }
  .banner-label { font-size: 12px; color: rgba(255,255,255,0.8); }
  .banner-pill {
    display: inline-block;
    background: rgba(255,255,255,0.2);
    border: 1px solid rgba(255,255,255,0.4);
    border-radius: 20px;
    padding: 3px 10px;
    font-size: 11px;
    font-weight: 600;
    margin-right: 8px;
  }
  .banner-desc { font-size: 11px; color: rgba(255,255,255,0.85); display: inline; }
  .banner-right { text-align: right; }
  .banner-year { font-size: 22px; font-weight: 700; }
  .banner-submitted { font-size: 10px; color: rgba(255,255,255,0.7); margin-top: 4px; }

  /* ── Stats Row ── */
  .stats-row {
    display: flex;
    gap: 12px;
    margin-bottom: 18px;
    page-break-inside: avoid;
  }
  .stat-card {
    flex: 1;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 18px;
  }
  .stat-num { font-size: 28px; font-weight: 700; }
  .stat-num.green { color: #16a34a; }
  .stat-num.amber { color: #d97706; }
  .stat-lbl { font-size: 10px; color: #64748b; margin-top: 2px; }

  /* ── Radar Chart ── */
  .chart-box {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 14px 16px;
    margin-bottom: 18px;
    page-break-inside: avoid;
  }
  .chart-box img { width: 60%; max-height: 260px; object-fit: contain; display: block; margin: 0 auto; }
  .chart-title { font-size: 13px; font-weight: 700; color: #1D3469; margin-bottom: 10px; }

  /* ── Section heading ── */
  .sec-title {
    font-size: 14px;
    font-weight: 700;
    color: #1D3469;
    border-bottom: 2px solid #e2e8f0;
    padding-bottom: 6px;
    margin-bottom: 14px;
    page-break-after: avoid;
  }

  /* ── Domain Block ── */
  .domain-block {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    padding: 14px 16px 16px;
    margin-bottom: 16px;
  }
  /* Keep domain header + bar together; allow two-col to break freely across pages */
  .domain-top { page-break-inside: avoid; }
  .domain-header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding-left: 10px;
    margin-bottom: 10px;
  }
  .domain-id-badge {
    width: 40px; height: 40px;
    border-radius: 8px;
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 700;
    flex-shrink: 0;
  }
  .domain-info { flex: 1; }
  .domain-name { font-size: 13px; font-weight: 700; color: #1D3469; }
  .domain-meta { display: flex; align-items: center; gap: 8px; margin-top: 3px; font-size: 10px; color: #64748b; }
  .lvl-pill { padding: 1px 8px; border-radius: 20px; font-size: 10px; font-weight: 600; }
  .meta-dot { color: #cbd5e1; }
  .domain-avg { font-size: 22px; font-weight: 700; text-align: right; line-height: 1; padding-top: 2px; }
  .domain-avg-sub { font-size: 11px; font-weight: 400; color: #94a3b8; }

  /* ── Score Bar ── */
  .bar-row { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
  .bar-track { flex: 1; height: 7px; background: #f1f5f9; border-radius: 4px; overflow: hidden; }
  .bar-fill { height: 100%; border-radius: 4px; }
  .bar-pct { font-size: 11px; font-weight: 600; width: 32px; text-align: right; }

  /* ── Two-column indicator lists ── */
  .two-col { display: flex; gap: 12px; }
  .col-panel { flex: 1; border-radius: 8px; padding: 10px 12px; }
  .green-panel { background: #f0fdf4; border: 1px solid #bbf7d0; }
  .amber-panel  { background: #fffbeb; border: 1px solid #fde68a; }
  .col-title { font-size: 11px; font-weight: 700; margin-bottom: 7px; }
  .green-title { color: #15803d; }
  .amber-title  { color: #b45309; }
  .ind-list { list-style: none; padding: 0; }
  .ind-item { display: flex; gap: 6px; align-items: flex-start; font-size: 10px; line-height: 1.6; margin-bottom: 4px; }
  .ind-empty { font-size: 10px; color: #94a3b8; font-style: italic; }
  .lvl-tag { flex-shrink: 0; padding: 0 5px; border-radius: 4px; font-size: 9px; font-weight: 700; margin-top: 1px; }
  .green-tag { background: #dcfce7; color: #15803d; }
  .amber-tag  { background: #fef3c7; color: #b45309; }

  /* ── Footer ── */
  .footer {
    margin-top: 24px;
    border-top: 1px solid #e2e8f0;
    padding-top: 10px;
    display: flex;
    justify-content: space-between;
    font-size: 9px;
    color: #94a3b8;
  }

  @media print {
    body { font-size: 11px; }
    .domain-top { page-break-inside: avoid; }
  }
</style>
</head>
<body>

<!-- Header Banner -->
<div class="header-banner">
    <div>
        <div class="banner-label">Overall School Quality Score</div>
        <div class="banner-score">${overallScore.toFixed(1)}%</div>
        <span class="banner-pill">L${overallMaturity} – ${MATURITY[overallMaturity]?.name}</span>
        <span class="banner-desc">${MATURITY[overallMaturity]?.description || ''}</span>
    </div>
    <div class="banner-right">
        <div class="banner-label">Academic Year</div>
        <div class="banner-year">${audit?.academic_year || '—'}</div>
        <div class="banner-submitted">${submittedStr}</div>
    </div>
</div>

<!-- Stats -->
<div class="stats-row">
    <div class="stat-card">
        <div class="stat-num green">${totalStrengths}</div>
        <div class="stat-lbl">Indicators at Level 3–4 (Strengths)</div>
    </div>
    <div class="stat-card">
        <div class="stat-num amber">${totalGaps}</div>
        <div class="stat-lbl">Indicators at Level 1–2 (Improvement Areas)</div>
    </div>
    <div class="stat-card">
        <div class="stat-num" style="color:#1D3469">${domainScores.length}</div>
        <div class="stat-lbl">Domains Evaluated</div>
    </div>
    <div class="stat-card">
        <div class="stat-num" style="color:#D67C2C">${domainScores.reduce((s, d) => s + d.totalIndicators, 0)}</div>
        <div class="stat-lbl">Total Indicators</div>
    </div>
</div>

${radarImgSrc ? `
<!-- Radar Chart -->
<div class="chart-box" style="margin-bottom:18px">
    <div class="chart-title">Maturity Profile Overview</div>
    <img src="${radarImgSrc}" alt="Maturity radar chart"/>
</div>` : ''}

<!-- Domain Analysis -->
<div class="sec-title">Detailed Domain Analysis</div>
${domainsHtml}

<!-- Footer -->
<div class="footer">
    <span>SQAAF School Quality Report – ${audit?.academic_year || ''}</span>
    <span>Generated ${dateStr} &nbsp;|&nbsp; Edumonics Platform</span>
</div>

</body>
</html>`;

        const win = window.open('', '_blank');
        if (!win) { alert('Pop-up blocked. Please allow pop-ups and try again.'); return; }
        win.document.write(html);
        win.document.close();
        win.focus();
        setTimeout(() => { win.print(); }, 800);
    };

    // Calculate Scores per Domain with detailed breakdown
    const domainScores = useMemo(() => {
        if (!audit || !domains || domains.length === 0) return [];

        return domains.map(domain => {
            const domainIndicators = (domain.subdomains || []).flatMap(s => s.indicators || []);
            const totalIndicators = domainIndicators.length;

            // Filter responses for this domain
            const domainResponses = (audit.responses || []).filter(r =>
                domainIndicators.some(i => i.indicator_id === r.indicator_id)
            );

            // Create detailed breakdown
            const indicatorDetails = domainIndicators.map(ind => {
                const response = domainResponses.find(r => r.indicator_id === ind.indicator_id);
                return {
                    id: ind.indicator_id,
                    statement: ind.statement,
                    level: response?.selected_level || 0,
                    hasEvidence: response?.evidence?.length > 0
                };
            });

            const strengths = indicatorDetails.filter(i => i.level >= 3);
            const gaps = indicatorDetails.filter(i => i.level > 0 && i.level < 3);
            const unanswered = indicatorDetails.filter(i => i.level === 0);

            const totalScore = domainResponses.reduce((sum, r) => sum + (r.selected_level || 0), 0);
            const averageLevel = totalIndicators > 0 ? (totalScore / totalIndicators) : 0;
            const maturityLevel = Math.round(averageLevel) || 1;

            return {
                id: domain.domain_id,
                name: domain.domain_name,
                weightage: domain.weightage || 10,
                score: parseFloat(averageLevel.toFixed(2)),
                maturityLevel,
                totalIndicators,
                answered: domainResponses.length,
                strengths,
                gaps,
                unanswered,
                subdomains: domain.subdomains?.map(sd => ({
                    name: sd.subdomain_name,
                    indicators: indicatorDetails.filter(i =>
                        sd.indicators?.some(si => si.indicator_id === i.id)
                    )
                })) || []
            };
        });
    }, [audit, domains]);

    // Wrap long domain names into two short lines for radar labels
    const wrapLabel = (name) => {
        const words = name.split(' ');
        if (words.length <= 2) return name;
        const mid = Math.ceil(words.length / 2);
        return [words.slice(0, mid).join(' '), words.slice(mid).join(' ')];
    };

    // Chart Data
    const chartData = {
        labels: domainScores.map(d => wrapLabel(d.name)),
        datasets: [
            {
                label: 'Current Maturity Level',
                data: domainScores.map(d => d.score),
                backgroundColor: 'rgba(214, 124, 44, 0.15)',
                borderColor: '#D67C2C',
                borderWidth: 2.5,
                pointBackgroundColor: '#1D3469',
                pointBorderColor: '#fff',
                pointBorderWidth: 2,
                pointRadius: 5,
                pointHoverRadius: 7,
                pointHoverBackgroundColor: '#fff',
                pointHoverBorderColor: '#D67C2C',
            },
        ],
    };

    const chartOptions = {
        maintainAspectRatio: false,
        layout: {
            padding: { top: 20, bottom: 20, left: 20, right: 20 },
        },
        scales: {
            r: {
                angleLines: { color: 'rgba(29, 52, 105, 0.12)' },
                grid: { color: 'rgba(29, 52, 105, 0.08)' },
                suggestedMin: 0,
                suggestedMax: 4,
                ticks: {
                    stepSize: 1,
                    backdropColor: 'transparent',
                    font: { size: 10, family: "'Poppins', sans-serif" },
                    color: '#9ca3af',
                },
                pointLabels: {
                    font: { size: 12, family: "'Poppins', sans-serif", weight: '600' },
                    color: '#1D3469',
                    padding: 12,
                },
            },
        },
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    title: (items) => {
                        const label = items[0]?.label;
                        return Array.isArray(label) ? label.join(' ') : label;
                    },
                    label: (item) => ` Level ${item.raw} / 4`,
                },
            },
        },
    };

    // Calculate overall score
    const overallScore = useMemo(() => {
        const totalWeightage = domainScores.reduce((sum, d) => sum + d.weightage, 0);
        const weightedSum = domainScores.reduce((sum, d) => sum + (d.score / 4) * d.weightage, 0);
        return totalWeightage > 0 ? (weightedSum / totalWeightage) * 100 : 0;
    }, [domainScores]);

    const overallMaturity = Math.round(domainScores.reduce((sum, d) => sum + d.score, 0) / domainScores.length) || 1;

    const getLevelBadge = (level) => {
        const maturity = MATURITY_LEVELS[Math.min(Math.max(Math.round(level), 1), 4)];
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-bold border ${maturity.color}`}>
                L{Math.round(level)} - {maturity.name}
            </span>
        );
    };

    return (
        <div className="w-full">
            <BackNav
                onBack={onBack}
                right={
                    <button
                        onClick={handleDownloadPDF}
                        disabled={isExporting}
                        className="flex items-center gap-2 bg-[#D67C2C] text-white px-4 py-2 rounded-lg hover:bg-[#c06a25] transition-colors disabled:opacity-50 text-sm font-medium cursor-pointer"
                    >
                        {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Export PDF
                    </button>
                }
            />

            <div ref={reportRef} className="px-6 py-6">
                {/* Overall Score Banner */}
                <div className="bg-gradient-to-r from-[#D67C2C] to-[#e89d4f] text-white p-6 rounded-2xl mb-8">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-orange-100 text-sm">Overall School Quality Score</p>
                            <p className="text-5xl font-bold mt-1">{overallScore.toFixed(1)}%</p>
                            <div className="flex items-center gap-3 mt-3">
                                {getLevelBadge(overallMaturity)}
                                <span className="text-orange-100 text-sm">
                                    {MATURITY_LEVELS[overallMaturity]?.description}
                                </span>
                            </div>
                        </div>
                        <div className="text-right">
                            <p className="text-orange-100 text-sm">Academic Year</p>
                            <p className="text-2xl font-bold">{audit?.academic_year}</p>
                            <p className="text-orange-200 text-sm mt-2">
                                {audit?.status === 'submitted'
                                    ? `Submitted ${new Date(audit.submitted_at).toLocaleDateString()}`
                                    : 'Draft - Ready for submission'
                                }
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
                    {/* Radar Chart */}
                    <div className="lg:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
                        <h3 className="text-lg font-bold text-[#1D3469] mb-4 flex items-center gap-2">
                            <Award className="w-5 h-5 text-[#D67C2C]" />
                            Maturity Profile Overview
                        </h3>
                        <div className="relative h-[420px] w-full">
                            <Radar data={chartData} options={chartOptions} />
                        </div>
                    </div>

                    {/* Quick Stats */}
                    <div className="space-y-4">
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                            <h4 className="font-semibold text-[#1D3469] mb-3 flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-green-500" />
                                Strengths
                            </h4>
                            <p className="text-4xl font-bold text-green-600">
                                {domainScores.reduce((sum, d) => sum + d.strengths.length, 0)}
                            </p>
                            <p className="text-sm text-gray-500">Indicators at Level 3-4</p>
                        </div>
                        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100">
                            <h4 className="font-semibold text-[#1D3469] mb-3 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                                Improvement Areas
                            </h4>
                            <p className="text-4xl font-bold text-amber-600">
                                {domainScores.reduce((sum, d) => sum + d.gaps.length, 0)}
                            </p>
                            <p className="text-sm text-gray-500">Indicators at Level 1-2</p>
                        </div>

                        {/* Submit Button */}
                        {audit?.status !== 'submitted' && (
                            <button
                                onClick={() => setShowConfirmModal(true)}
                                disabled={isSubmitting}
                                className="w-full bg-[#D67C2C] hover:bg-[#c06928] text-white font-semibold py-4 px-4 rounded-xl flex items-center justify-center gap-2 transition-all"
                            >
                                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                                {isSubmitting ? 'Submitting...' : 'Submit Final Audit'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Detailed Domain Breakdown */}
                <h3 className="text-lg font-bold text-[#1D3469] mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-[#D67C2C]" />
                    Detailed Domain Analysis
                </h3>

                <div className="space-y-4">
                    {domainScores.map((domain) => {
                        const isExpanded = expandedDomain === domain.id;
                        const maturity = MATURITY_LEVELS[domain.maturityLevel];

                        return (
                            <div key={domain.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <button
                                    onClick={() => setExpandedDomain(isExpanded ? null : domain.id)}
                                    className="w-full flex items-center justify-between p-5 hover:bg-gray-50 transition-colors"
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${domain.score >= 3 ? 'bg-green-100 text-green-700' :
                                                domain.score >= 2 ? 'bg-amber-100 text-amber-700' :
                                                    'bg-red-100 text-red-700'
                                            }`}>
                                            {domain.id}
                                        </div>
                                        <div className="text-left">
                                            <h4 className="font-semibold text-[#1D3469]">{domain.name}</h4>
                                            <div className="flex items-center gap-3 mt-1">
                                                {getLevelBadge(domain.score)}
                                                <span className="text-xs text-gray-400">
                                                    {domain.answered}/{domain.totalIndicators} indicators answered
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="text-right hidden md:block">
                                            <p className="text-2xl font-bold text-[#D67C2C]">{domain.score.toFixed(1)}</p>
                                            <p className="text-xs text-gray-400">Avg Level</p>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm">
                                            <span className="px-2 py-1 bg-green-50 text-green-700 rounded">
                                                ✓ {domain.strengths.length}
                                            </span>
                                            <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded">
                                                ⚠ {domain.gaps.length}
                                            </span>
                                        </div>
                                        {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                                    </div>
                                </button>

                                {isExpanded && (
                                    <div className="border-t border-gray-100 p-5 bg-gray-50">
                                        {/* Maturity Description */}
                                        <div className={`p-4 rounded-lg border mb-6 ${maturity.color}`}>
                                            <p className="font-medium">{maturity.name} Level</p>
                                            <p className="text-sm mt-1 opacity-80">{maturity.description}</p>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                            {/* Strengths */}
                                            <div>
                                                <h5 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                                                    <CheckCircle className="w-4 h-4" />
                                                    Strengths ({domain.strengths.length})
                                                </h5>
                                                {domain.strengths.length === 0 ? (
                                                    <p className="text-sm text-gray-400 italic">No indicators at Level 3-4 yet</p>
                                                ) : (
                                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                                        {domain.strengths.map(ind => (
                                                            <div key={ind.id} className="bg-green-50 p-3 rounded-lg border border-green-100">
                                                                <div className="flex justify-between items-start gap-2">
                                                                    <p className="text-sm text-green-800">{ind.statement}</p>
                                                                    <span className="flex-shrink-0 px-2 py-0.5 bg-green-200 text-green-800 rounded text-xs font-bold">
                                                                        L{ind.level}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {/* Gaps */}
                                            <div>
                                                <h5 className="font-semibold text-amber-700 mb-3 flex items-center gap-2">
                                                    <Lightbulb className="w-4 h-4" />
                                                    Areas for Improvement ({domain.gaps.length})
                                                </h5>
                                                {domain.gaps.length === 0 ? (
                                                    <p className="text-sm text-gray-400 italic">Excellent! No major gaps identified</p>
                                                ) : (
                                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                                        {domain.gaps.map(ind => (
                                                            <div key={ind.id} className="bg-amber-50 p-3 rounded-lg border border-amber-100">
                                                                <div className="flex justify-between items-start gap-2">
                                                                    <p className="text-sm text-amber-800">{ind.statement}</p>
                                                                    <span className="flex-shrink-0 px-2 py-0.5 bg-amber-200 text-amber-800 rounded text-xs font-bold">
                                                                        L{ind.level}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Submit Confirmation Modal */}
            {showConfirmModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-2xl p-8 max-w-md mx-4 shadow-2xl">
                        <h3 className="text-xl font-bold text-gray-900 mb-4">Submit Final Audit?</h3>
                        <p className="text-gray-600 mb-6">
                            Once submitted, you won't be able to edit responses. Your scores will be calculated and the audit will be marked as complete.
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowConfirmModal(false)}
                                className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitAudit}
                                disabled={isSubmitting}
                                className="flex-1 py-3 px-4 bg-[#D67C2C] text-white rounded-xl font-medium hover:bg-[#c06928] flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />}
                                {isSubmitting ? 'Submitting...' : 'Confirm Submit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SQAAFScoreReport;
